import { createContext, useState, useEffect } from "react";
import english from "../AllLanguages/english";
import italiano from "../AllLanguages/italiano";
import deutsch from "../AllLanguages/deutsch";
import español from "../AllLanguages/español";
import francais from "../AllLanguages/francais";
import nihongo from "../AllLanguages/nihongo";
import nederlands from "../AllLanguages/nederlands";
import chinese from "../AllLanguages/chinese";
import axios from "../AxiosInstance";

export const LanguageContext = createContext();

const languageData = {
    english,
    italiano,
    deutsch,
    español,
    francais,
    nihongo,
    nederlands,
    chinese
};

// Map country codes to language codes
const countryToLanguage = {
    // Dutch-speaking countries
    'NL': 'nederlands', // Netherlands
    'BE': 'nederlands', // Belgium (Flanders)
    'SR': 'nederlands', // Suriname
    'CW': 'nederlands', // Curaçao
    'AW': 'nederlands', // Aruba
    'SX': 'nederlands', // Sint Maarten
    
    // Chinese-speaking countries/regions
    'CN': 'chinese', // China
    'TW': 'chinese', // Taiwan
    'HK': 'chinese', // Hong Kong
    'MO': 'chinese', // Macau
    'SG': 'chinese', // Singapore (has significant Chinese population)
    
    // German-speaking countries
    'DE': 'deutsch', // Germany
    'AT': 'deutsch', // Austria
    'CH': 'deutsch', // Switzerland
    'LI': 'deutsch', // Liechtenstein
    
    // Spanish-speaking countries
    'ES': 'español', // Spain
    'MX': 'español', // Mexico
    'AR': 'español', // Argentina
    'CO': 'español', // Colombia
    'CL': 'español', // Chile
    'PE': 'español', // Peru
    'VE': 'español', // Venezuela
    'EC': 'español', // Ecuador
    'GT': 'español', // Guatemala
    'CU': 'español', // Cuba
    'BO': 'español', // Bolivia
    'DO': 'español', // Dominican Republic
    'HN': 'español', // Honduras
    'PY': 'español', // Paraguay
    'SV': 'español', // El Salvador
    'NI': 'español', // Nicaragua
    'CR': 'español', // Costa Rica
    'PA': 'español', // Panama
    'UY': 'español', // Uruguay
    'GQ': 'español', // Equatorial Guinea
    
    // French-speaking countries
    'FR': 'francais', // France
    'BE': 'francais', // Belgium (Wallonia - but BE is already mapped to Dutch, so French speakers in BE would need manual selection)
    'CH': 'francais', // Switzerland (French-speaking regions)
    'CA': 'francais', // Canada (Quebec)
    'LU': 'francais', // Luxembourg
    
    // Italian-speaking countries
    'IT': 'italiano', // Italy
    'SM': 'italiano', // San Marino
    'VA': 'italiano', // Vatican City
    
    // Japanese-speaking countries
    'JP': 'nihongo', // Japan
};

// Map browser language codes to our language codes
const browserLangToLanguage = {
    'en': 'english',
    'nl': 'nederlands',
    'zh': 'chinese',
    'zh-CN': 'chinese',
    'zh-TW': 'chinese',
    'zh-HK': 'chinese',
    'de': 'deutsch',
    'es': 'español',
    'fr': 'francais',
    'it': 'italiano',
    'ja': 'nihongo',
};

// Auto-detect language based on location and browser settings
const detectLanguage = async () => {
    // First, check if user has manually selected a language
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && savedLanguage !== "auto") {
        return savedLanguage;
    }

    try {
        // Try to detect country from IP
        const res = await fetch("https://ipwho.is/");
        const data = await res.json();
        
        if (data && data.country_code) {
            const countryCode = data.country_code;
            const detectedLang = countryToLanguage[countryCode];
            
            if (detectedLang && languageData[detectedLang]) {
                return detectedLang;
            }
        }
    } catch (err) {
        console.error("Failed to detect country from IP:", err);
    }

    // Fallback to browser language
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    const langCode = browserLang.split('-')[0].toLowerCase();
    const detectedLang = browserLangToLanguage[browserLang] || browserLangToLanguage[langCode];
    
    return detectedLang || 'english';
};

export const LanguageProvider = ({ children }) => {
    const [selectedLanguage, setSelectedLanguage] = useState("english");
    const [isDetecting, setIsDetecting] = useState(true);

    useEffect(() => {
        const initializeLanguage = async () => {
            const savedLanguage = localStorage.getItem("language");
            
            if (savedLanguage && savedLanguage !== "auto") {
                setSelectedLanguage(savedLanguage);
                setIsDetecting(false);
            } else {
                // Auto-detect language
                const detectedLang = await detectLanguage();
                setSelectedLanguage(detectedLang);
                localStorage.setItem("language", detectedLang);
                setIsDetecting(false);
            }
        };

        initializeLanguage();
    }, []);

    useEffect(() => {
        if (!isDetecting && selectedLanguage) {
        localStorage.setItem("language", selectedLanguage);
            
            // Sync language preference with backend if user is logged in
            const authData = localStorage.getItem("auth");
            if (authData) {
                try {
                    const parseData = JSON.parse(authData);
                    const userId = parseData?.user?._id;
                    if (userId) {
                        // Update user's language preference in backend
                        axios.put(`/api/v1/auth/update-profile/${userId}`, {
                            language: selectedLanguage
                        }).catch(err => {
                            console.error("Failed to sync language preference:", err);
                        });
                    }
                } catch (err) {
                    console.error("Error parsing auth data:", err);
                }
            }
        }
    }, [selectedLanguage, isDetecting]);

    const value = {
        selectedLanguage,
        setSelectedLanguage,
        lg: languageData[selectedLanguage],
    };
    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
